import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './accountsManager.module.scss';
import TitleSectionBack from 'Containers/Common/TitleSectionBack';
import { modalActions } from 'Containers/Modals/store';
import { useDispatch, useSelector } from 'react-redux';
import useWindowSize from '../../../../utils/useWindowSize';
import ConfirmDeleteModal from 'Containers/Common/ConfirmDeleteModal';
import { memo, useEffect, useState } from 'react';
import { AccountItem } from './AccountItem';
import { getAccountsStaff } from 'Containers/Settings/store/selectors';
import { settingsActions } from 'Containers/Settings/store';
import { Plus, UserPlus, Users } from 'lucide-react';

const AccountsManager = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const accounts = useSelector(getAccountsStaff());

  useEffect(() => {
    dispatch(settingsActions.GET_ACCOUNTS_STAFF.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch]);

  const openNewAccountModal = () => {
    dispatch(modalActions.OPEN_MODAL.SUCCESS({ type: 'accountModal' }));
  };

  return (
    <div
      className={clsx(styles.manager_container, {
        [styles.manager_container_mbl]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('settings.accountsManager.title')}
          description={t('settings.accountsManager.description')}
        />
        {!isMobile && accounts.length > 0 ? (
          <button type="button" className={styles.add_button} onClick={openNewAccountModal}>
            <Plus size={18} strokeWidth={2.4} />
            <span>{t('settings.accountsManager.noAccounts.addCollaborator')}</span>
          </button>
        ) : null}
      </div>

      {accounts.length > 0 ? (
        <>
          <div className={styles.list_meta}>
            <Users size={16} strokeWidth={2.2} aria-hidden />
            <span>
              {t('settings.accountsManager.count', { count: accounts.length })}
            </span>
          </div>
          <AccountsList accounts={accounts} />
        </>
      ) : (
        <div className={styles.empty_state}>
          <div className={styles.empty_icon} aria-hidden>
            <UserPlus size={28} strokeWidth={2} />
          </div>
          <h2 className={styles.empty_title}>
            {t('settings.accountsManager.noAccounts.title')}
          </h2>
          <p className={styles.empty_description}>
            {t('settings.accountsManager.noAccounts.description')}
          </p>
          <button type="button" className={styles.empty_cta} onClick={openNewAccountModal}>
            <Plus size={18} strokeWidth={2.4} />
            <span>{t('settings.accountsManager.noAccounts.addCollaborator')}</span>
          </button>
        </div>
      )}

      {isMobile && accounts.length > 0 ? (
        <button
          type="button"
          className={styles.fab}
          onClick={openNewAccountModal}
          aria-label={t('settings.accountsManager.noAccounts.addCollaborator')}
        >
          <Plus size={26} strokeWidth={2.4} />
        </button>
      ) : null}
    </div>
  );
};

const AccountsList = memo(
  ({ accounts = [] }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [items, setItems] = useState(accounts);
    const [accountToDelete, setAccountToDelete] = useState(false);

    useEffect(() => {
      setItems(accounts);
    }, [accounts]);

    const openEditAccountModal = (account) => {
      dispatch(
        modalActions.OPEN_MODAL.SUCCESS({
          type: 'accountModal',
          props: { account },
        }),
      );
    };

    return (
      <>
        <ConfirmDeleteModal
          isModalOpen={accountToDelete}
          setIsModalOpen={setAccountToDelete}
          title={t('settings.accountsManager.confirmDelete.title', {
            name: accountToDelete?.nome_referente,
          })}
          buttons={[t('common.actions.cancel'), t('common.actions.proceed')]}
          action={() => {
            dispatch(
              settingsActions.DELETE_ACCOUNT_STAFF.REQUEST(
                {
                  idUtenteBusiness: accountToDelete.idUtenteBusiness,
                },
                () => setAccountToDelete(false),
              ),
            );
          }}
        />
        <div className={styles.accounts_list}>
          {items.map((account) => (
            <AccountItem
              key={account.idUtenteBusiness}
              account={account}
              openEditAccountModal={openEditAccountModal}
              setAccountToDelete={setAccountToDelete}
            />
          ))}
        </div>
      </>
    );
  },
  (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps),
);

export default AccountsManager;
